import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';

const DataManagementSection = ({ onExport, onImport }) => {
  const { user, userProfile } = useAuth();
  const [isExporting, setIsExporting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [exportSuccess, setExportSuccess] = useState(false);
  const [importSuccess, setImportSuccess] = useState(false);

  // Master delete states
  const [deleteStep, setDeleteStep] = useState(0); // 0=initial, 1=warning, 2=confirm, 3=type-confirm
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [deleteSuccess, setDeleteSuccess] = useState(false);

  const REQUIRED_CONFIRM_TEXT = 'DELETE ALL MY DATA';

  const handleExport = async () => {
    setIsExporting(true);
    setExportSuccess(false);
    
    setTimeout(() => {
      onExport();
      setIsExporting(false);
      setExportSuccess(true);
      setTimeout(() => setExportSuccess(false), 3000);
    }, 1500);
  };

  const handleImport = async (event) => {
    const file = event?.target?.files?.[0];
    if (!file) return;

    setIsImporting(true);
    setImportSuccess(false);

    setTimeout(() => {
      onImport(file);
      setIsImporting(false);
      setImportSuccess(true);
      setTimeout(() => setImportSuccess(false), 3000);
      event.target.value = '';
    }, 1500);
  };

  const handleMasterDelete = async () => {
    if (!userProfile?.employee_id) {
      setDeleteError('Employee ID not found');
      return;
    }

    setIsDeleting(true);
    setDeleteError('');

    try {
      const { data, error } = await supabase?.rpc('master_delete_user_data', {
        target_employee_id: userProfile?.employee_id
      });

      if (error) throw error;

      setDeleteSuccess(true);
      setDeleteStep(0);
      setDeleteConfirmText('');

      // Reload page after 2 seconds to reflect changes
      setTimeout(() => {
        window.location?.reload();
      }, 2000);
    } catch (err) {
      setDeleteError(err?.message || 'Failed to delete data');
    } finally {
      setIsDeleting(false);
    }
  };

  const resetDeleteFlow = () => {
    setDeleteStep(0);
    setDeleteConfirmText('');
    setDeleteError('');
    setDeleteSuccess(false);
  };

  return (
    <div className="card">
      <div className="flex items-center gap-3 mb-6 md:mb-8">
        <div className="flex items-center justify-center w-10 h-10 md:w-12 md:h-12 rounded-xl bg-accent-pink/20 glow-subtle">
          <Icon name="Database" size={20} color="var(--color-accent-pink)" />
        </div>
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-foreground">Data Management</h2>
          <p className="text-xs md:text-sm text-muted-foreground font-caption">
            Backup and restore your settings
          </p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
        <div className="p-4 md:p-6 rounded-lg bg-tertiary border border-accent-cyan/20">
          <div className="flex items-center gap-3 mb-4">
            <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-accent-cyan/20">
              <Icon name="Download" size={20} color="var(--color-accent-cyan)" />
            </div>
            <div>
              <h3 className="text-base md:text-lg font-semibold text-foreground">Export Settings</h3>
              <p className="text-xs md:text-sm text-muted-foreground">
                Download your configuration
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            fullWidth
            iconName="Download"
            iconPosition="left"
            loading={isExporting}
            onClick={handleExport}
            className="mt-4"
          >
            {isExporting ? 'Exporting...' : 'Export Settings'}
          </Button>
          {exportSuccess && (
            <div className="mt-3 p-3 rounded-lg bg-success/10 border border-success/20 flex items-center gap-2">
              <Icon name="CheckCircle" size={16} color="var(--color-success)" />
              <span className="text-sm text-success">Settings exported successfully</span>
            </div>
          )}
        </div>

        <div className="p-4 md:p-6 rounded-lg bg-tertiary border border-accent-blue/20">
          <div className="flex items-center gap-3 mb-4">
            <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-accent-blue/20">
              <Icon name="Upload" size={20} color="var(--color-accent-blue)" />
            </div>
            <div>
              <h3 className="text-base md:text-lg font-semibold text-foreground">Import Settings</h3>
              <p className="text-xs md:text-sm text-muted-foreground">
                Restore from backup file
              </p>
            </div>
          </div>
          <label className="block mt-4">
            <input
              type="file"
              accept=".json"
              onChange={handleImport}
              className="hidden"
              disabled={isImporting}
            />
            <Button
              variant="outline"
              fullWidth
              iconName="Upload"
              iconPosition="left"
              loading={isImporting}
              asChild
            >
              {isImporting ? 'Importing...' : 'Import Settings'}
            </Button>
          </label>
          {importSuccess && (
            <div className="mt-3 p-3 rounded-lg bg-success/10 border border-success/20 flex items-center gap-2">
              <Icon name="CheckCircle" size={16} color="var(--color-success)" />
              <span className="text-sm text-success">Settings imported successfully</span>
            </div>
          )}
        </div>
      </div>
      {/* Master Delete Section */}
      <div className="mt-6 p-4 md:p-6 rounded-lg bg-error/10 border-2 border-error/30">
        <div className="flex items-center gap-3 mb-4">
          <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-error/20">
            <Icon name="Trash2" size={20} color="var(--color-error)" />
          </div>
          <div>
            <h3 className="text-base md:text-lg font-semibold text-foreground">Master Delete</h3>
            <p className="text-xs md:text-sm text-muted-foreground">
              Permanently erase all your data
            </p>
          </div>
        </div>

        {deleteStep === 0 && (
          <>
            <div className="mb-4 p-3 rounded-lg bg-warning/10 border border-warning/20">
              <div className="flex items-start gap-2">
                <Icon name="AlertTriangle" size={18} color="var(--color-warning)" className="flex-shrink-0 mt-0.5" />
                <div className="text-xs md:text-sm text-muted-foreground">
                  <p className="font-medium text-foreground mb-1">What will be deleted:</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>All flight history and rotations</li>
                    <li>All timecard data and pay calculations</li>
                    <li>All manual events and adjustments</li>
                    <li>All operational preferences and settings</li>
                  </ul>
                  <p className="font-medium text-foreground mt-2 mb-1">What will be kept:</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>Your name</li>
                    <li>Employee number</li>
                    <li>Email address</li>
                  </ul>
                </div>
              </div>
            </div>
            <Button
              variant="outline"
              fullWidth
              iconName="Trash2"
              iconPosition="left"
              onClick={() => setDeleteStep(1)}
              className="border-error/50 text-error hover:bg-error/10"
            >
              Begin Master Delete
            </Button>
          </>
        )}

        {deleteStep === 1 && (
          <>
            <div className="mb-4 p-4 rounded-lg bg-error/20 border border-error/40">
              <div className="flex items-start gap-2">
                <Icon name="AlertTriangle" size={20} color="var(--color-error)" className="flex-shrink-0 mt-0.5" />
                <div className="text-sm text-foreground">
                  <p className="font-bold mb-2">⚠️ CRITICAL WARNING ⚠️</p>
                  <p className="mb-2">
                    This action is <span className="font-bold underline">IRREVERSIBLE</span>. All your flight data, 
                    pay history, rotations, and settings will be permanently deleted.
                  </p>
                  <p className="font-medium">
                    Only your name, employee number, and email will remain.
                  </p>
                </div>
              </div>
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                fullWidth
                onClick={resetDeleteFlow}
              >
                Cancel
              </Button>
              <Button
                variant="outline"
                fullWidth
                onClick={() => setDeleteStep(2)}
                className="border-error/50 text-error hover:bg-error/10"
              >
                I Understand, Continue
              </Button>
            </div>
          </>
        )}

        {deleteStep === 2 && (
          <>
            <div className="mb-4 p-4 rounded-lg bg-error/20 border border-error/40">
              <div className="flex items-start gap-2">
                <Icon name="ShieldAlert" size={20} color="var(--color-error)" className="flex-shrink-0 mt-0.5" />
                <div className="text-sm text-foreground">
                  <p className="font-bold mb-2">Final Confirmation Required</p>
                  <p className="mb-3">
                    To proceed with permanent deletion, type the following text exactly:
                  </p>
                  <div className="p-2 rounded bg-background/50 border border-error/30 font-mono text-center text-error font-bold">
                    {REQUIRED_CONFIRM_TEXT}
                  </div>
                </div>
              </div>
            </div>
            <input
              type="text"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e?.target?.value)}
              placeholder="Type confirmation text here"
              className="w-full px-4 py-2 mb-4 rounded-lg bg-background border border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-error/50"
            />
            <div className="flex gap-3">
              <Button
                variant="outline"
                fullWidth
                onClick={resetDeleteFlow}
              >
                Cancel
              </Button>
              <Button
                variant="outline"
                fullWidth
                onClick={handleMasterDelete}
                disabled={deleteConfirmText !== REQUIRED_CONFIRM_TEXT || isDeleting}
                loading={isDeleting}
                className="border-error/50 text-error hover:bg-error/10 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isDeleting ? 'Deleting...' : 'Delete All My Data'}
              </Button>
            </div>
          </>
        )}

        {deleteError && (
          <div className="mt-3 p-3 rounded-lg bg-error/10 border border-error/20 flex items-center gap-2">
            <Icon name="XCircle" size={16} color="var(--color-error)" />
            <span className="text-sm text-error">{deleteError}</span>
          </div>
        )}

        {deleteSuccess && (
          <div className="mt-3 p-3 rounded-lg bg-success/10 border border-success/20 flex items-center gap-2">
            <Icon name="CheckCircle" size={16} color="var(--color-success)" />
            <span className="text-sm text-success">All data deleted successfully. Reloading...</span>
          </div>
        )}
      </div>
      <div className="mt-6 p-4 rounded-lg bg-warning/10 border border-warning/20">
        <div className="flex items-start gap-3">
          <Icon name="AlertTriangle" size={20} color="var(--color-warning)" className="flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-foreground mb-2">Security Notice</p>
            <p className="text-xs md:text-sm text-muted-foreground">
              Exported settings files contain your personal information and pay rates. Store them securely and never share them with unauthorized parties. Import only files you have exported yourself.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataManagementSection;